import { useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import { PrintService } from '../services/printService';
import { Container } from '../interfaces/container.interface';
import { ReceptionContainer } from '../interfaces/receptionCotnainer.interface';
import { Article } from '../interfaces/article.interface';
import OriginContainerLabel from '../../ui/components/print/OriginContainerLabel';
import ArticleLabel from '../../ui/components/print/ArticleLabel';
import SmallContainerLabel from '../../ui/components/print/SmallContainerLabel';
import DestinationContainerLabel from '../../ui/components/print/DestinationContainerLabel';
import ModernContainerLabel from '../../ui/components/print/ModernContainerLabel';
import { useMasterCore } from '../modules/master';

export type PrintableItem = Container | ReceptionContainer | Article;

export interface PrintLabelOptions {
    size?: 'large' | 'small';
    type?: 'destination' | 'origin' | 'article' | 'container';
}

export const usePrintLabel = () => {
    const { containerCategories, containerTypes, channelTypes, priceRanges } = useMasterCore();

    console.log("containerCategories", containerCategories);
    console.log("containerTypes", containerTypes);
    console.log("channelTypes", channelTypes);
    console.log("priceRanges", priceRanges);
    // Helper functions para buscar por ID
    const findCategoryById = (id: number) => containerCategories.find(cat => cat.id === id);
    const findTypeById = (id: number) => containerTypes.find(type => type.id === id);
    const findChannelById = (id: number) => channelTypes.find(channel => channel.id === id);
    const findPriceRangeById = (id: number) => priceRanges.find(range => range.id === id);

    const printDestinationContainer = useCallback((container: Container) => {
        // Obtener datos reales del container
        const category = findCategoryById(container.container_category_id);
        
        // El container_type_id se obtiene desde la categoría, no directamente del container
        const containerTypeId = (category as any)?.container_type_id || container.container_type_id;
        const type = findTypeById(containerTypeId);
        const channel = findChannelById(container.channel_type_id);
        const priceRange = findPriceRangeById(container.price_range_id);
        
        const categoryName = category?.name || 'CONTENEDOR';
        const typeName = type?.name || 'DESTINO';
        const channelName = channel?.name || 'ECOMMERCE';
        
        // Formatear rango de precios
        const priceRangeText = priceRange 
            ? `$${priceRange.start_price.toLocaleString()} - $${priceRange.end_price.toLocaleString()}`
            : '$0 - $20.000';

        const labelComponent = React.createElement(ModernContainerLabel, {
            container,
            categoryName,
            typeName,
            channelName,
            priceRange: priceRangeText,
            theme: 'mono'
        });
        const htmlContent = renderToStaticMarkup(labelComponent);
        PrintService.printLabel(htmlContent, 'large');
    }, [containerCategories, containerTypes, channelTypes, priceRanges]);

    const renderOriginContainerLabel = useCallback((container: ReceptionContainer, supplier = 'PROVEEDOR XYZ', area = 'ÁREA RECEPCIÓN') => {
        const labelComponent = React.createElement(OriginContainerLabel, {
            container,
            supplier,
            area
        });
        return renderToStaticMarkup(labelComponent);
    }, []);

    const printOriginContainer = useCallback((container: ReceptionContainer) => {
        const htmlContent = renderOriginContainerLabel(container);
        PrintService.printLabel(htmlContent, 'large');
    }, [renderOriginContainerLabel]);

    const printArticle = useCallback((article: Article) => {
        const labelComponent = React.createElement(ArticleLabel, { article });
        const htmlContent = renderToStaticMarkup(labelComponent);
        PrintService.printLabel(htmlContent, 'small');
    }, []);

    const printSmallContainer = useCallback((container: Container) => {
        // Obtener datos reales del container
        const category = findCategoryById(container.container_category_id);
        
        // El container_type_id se obtiene desde la categoría, no directamente del container
        const containerTypeId = (category as any)?.container_type_id || container.container_type_id;
        const type = findTypeById(containerTypeId);
        const channel = findChannelById(container.channel_type_id);
        const priceRange = findPriceRangeById(container.price_range_id);

        console.log("category", category);
        console.log("type", type);
        console.log("channel", channel);
        console.log("priceRange", priceRange);
        console.log("container", container);
        console.log("containerTypeId from category:", containerTypeId);

        
        const categoryName = category?.name || 'CONTENEDOR';
        const typeName = type?.name || 'CONTENEDOR';
        const channelName = channel?.name || 'ECOMMERCE';
        
        // Formatear rango de precios
        const priceRangeText = priceRange 
            ? `$${priceRange.start_price.toLocaleString()} - $${priceRange.end_price.toLocaleString()}`
            : '$0 - $20.000';

        const labelComponent = React.createElement(ModernContainerLabel, {
            container,
            categoryName,
            typeName,
            channelName,
            priceRange: priceRangeText,
            theme: 'mono'
        });
        const htmlContent = renderToStaticMarkup(labelComponent);
        PrintService.printLabel(htmlContent, 'small');
    }, [containerCategories, containerTypes, channelTypes, priceRanges]);

    const printLabel = useCallback((item: PrintableItem, options: PrintLabelOptions = {}) => {
        const { type, size } = options;

        // Si es un artículo
        if ('name' in item && 'brand' in item) {
            printArticle(item as Article);
            return;
        }

        // Si es un contenedor
        if ('code' in item) {
            // Determinar si es contenedor de origen o destino
            const container = item as Container | ReceptionContainer;

            // Si tiene correlative_id es un ReceptionContainer (origen)
            if ('correlative_id' in container) {
                printOriginContainer(container as ReceptionContainer);
                return;
            }

            // Si es un Container normal
            const normalContainer = container as Container;

            // Determinar tamaño basado en las opciones o tipo de contenedor
            if (size === 'small' || type === 'container') {
                printSmallContainer(normalContainer);
            } else {
                printDestinationContainer(normalContainer);
            }
        }
    }, [printDestinationContainer, printOriginContainer, printArticle, printSmallContainer]);

    // Función para imprimir múltiples etiquetas
    const printMultipleLabels = useCallback((items: PrintableItem[], options: PrintLabelOptions = {}) => {
        if (!items || items.length === 0) {
            return;
        }

        const allOriginContainers = items.every((item) => 'correlative_id' in (item as any));

        if (allOriginContainers) {
            const htmlLabels = (items as ReceptionContainer[]).map((originContainer) => {
                const markup = renderOriginContainerLabel(originContainer);
                return `<div style="page-break-after: always;">${markup}</div>`;
            });

            const htmlContent = htmlLabels.join('');
            PrintService.printLabel(htmlContent, 'large');
            return;
        }

        items.forEach((item, index) => {
            // Agregar un pequeño delay entre impresiones para evitar problemas con popups
            setTimeout(() => {
                printLabel(item, options);
            }, index * 500);
        });
    }, [printLabel, renderOriginContainerLabel]);

    return {
        printLabel,
        printMultipleLabels,
        printDestinationContainer,
        printOriginContainer,
        printArticle,
        printSmallContainer,
        renderOriginContainerLabel,
    };
};

export default usePrintLabel; 